// Reusable comment thread + reply box. Renders public comments (and, when
// `showInternal`, internal team notes styled distinctly). Used by the public
// portal and the agent detail view.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { brand } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import { formatDate, type Comment, type Attachment } from "../services/serviceDeskService";

function AttachmentList({ attachments }: { attachments?: Attachment[] }) {
  const { isDarkMode } = useTheme();

  if (!attachments || attachments.length === 0) return null;

  const isImage = (contentType?: string | null) => {
    return contentType?.startsWith("image/");
  };

  const isVideo = (contentType?: string | null) => {
    return contentType?.startsWith("video/");
  };

  const images = attachments.filter(a => isImage(a.contentType));
  const videos = attachments.filter(a => isVideo(a.contentType));
  const files = attachments.filter(a => !isImage(a.contentType) && !isVideo(a.contentType));

  return (
    <div className="mt-2 space-y-3">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {images.map((a, i) => (
            <div key={i} className="inline-block">
              <img
                src={a.url}
                alt={a.name}
                className={`max-h-64 max-w-sm rounded-lg border ${isDarkMode ? 'border-dark-border' : 'border-gray-300'} shadow-md`}
              />
              <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{a.name}</p>
            </div>
          ))}
        </div>
      )}
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {videos.map((a, i) => (
            <div key={i} className="inline-block">
              <video
                src={a.url}
                controls
                className={`max-h-64 max-w-sm rounded-lg border ${isDarkMode ? 'border-dark-border' : 'border-gray-300'} shadow-md bg-black`}
              />
              <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{a.name}</p>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((a, i) => (
            <a
              key={i}
              href={a.url}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'border-dark-border bg-dark-border text-gray-200 hover:bg-gray-700'
                  : 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-attachment-2" /> {a.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommentThread({
  comments,
  onReply,
  replyLabel = "Escribe un comentario…",
  showInternal = false,
  allowInternalToggle = false,
  internalOnly = false,
  emptyLabel,
}: {
  comments: Comment[];
  onReply?: (body: string, files: File[], internal: boolean) => Promise<void>;
  replyLabel?: string;
  showInternal?: boolean;
  allowInternalToggle?: boolean;
  /**
   * Render ONLY internal team notes and force every reply to be an internal
   * note (no public/internal toggle). Used by the dedicated "Notas internas"
   * section so internal notes live apart from the public client thread (#34).
   */
  internalOnly?: boolean;
  emptyLabel?: string;
}) {
  const { t } = useTranslation("serviceDesk");
  const { isDarkMode } = useTheme();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [internal, setInternal] = useState(internalOnly);
  const [sending, setSending] = useState(false);

  // internalOnly → just the team notes; otherwise honor showInternal for
  // whether internal notes are interleaved with the public thread.
  const visible = comments.filter((c) =>
    internalOnly ? c.internal : showInternal || !c.internal,
  );

  // A reply is internal when the thread is internal-only or the agent ticked
  // the toggle.
  const isInternalReply = internalOnly || internal;

  const send = async () => {
    if (!onReply || (!body.trim() && files.length === 0)) return;
    setSending(true);
    try {
      await onReply(body.trim(), files, isInternalReply);
      setBody("");
      setFiles([]);
      setInternal(internalOnly);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="space-y-3">
        {visible.length === 0 && (
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {emptyLabel || (internalOnly ? t("ticketDetail.comments.noInternalNotes") : t("ticketDetail.comments.noComments"))}
          </p>
        )}
        {visible.map((c) => {
          const isAgent = c.authorType === "agent";
          // Respuesta automática con IA: la escribe sólo el servidor.
          const isAi = c.authorType === "ai_agent" || c.authorIsAi === true;
          const isInternal = Boolean(c.internal);

          let bgClass = "";
          let borderClass = "";
          let authorClass = "";
          let timeClass = "";

          if (isInternal) {
            bgClass = isDarkMode ? "bg-amber-950/40" : "bg-amber-50";
            borderClass = isDarkMode ? "border-amber-900/50" : "border-amber-200";
            authorClass = isDarkMode ? "text-amber-200" : "text-gray-700";
            timeClass = isDarkMode ? "text-amber-700" : "text-gray-400";
          } else if (isAi) {
            bgClass = isDarkMode ? "bg-violet-950/30" : "bg-violet-50/60";
            borderClass = isDarkMode ? "border-violet-900/40" : "border-violet-200";
            authorClass = isDarkMode ? "text-violet-100" : "text-gray-700";
            timeClass = isDarkMode ? "text-violet-700" : "text-gray-400";
          } else if (isAgent) {
            bgClass = isDarkMode ? "bg-gray-800/50" : "bg-gray-50";
            borderClass = isDarkMode ? "border-dark-border" : "border-gray-200";
            authorClass = isDarkMode ? "text-gray-100" : "text-gray-700";
            timeClass = isDarkMode ? "text-gray-500" : "text-gray-400";
          } else {
            bgClass = isDarkMode ? "bg-blue-950/30" : "bg-blue-50/40";
            borderClass = isDarkMode ? "border-blue-900/40" : "border-blue-100";
            authorClass = isDarkMode ? "text-blue-100" : "text-gray-700";
            timeClass = isDarkMode ? "text-blue-700" : "text-gray-400";
          }

          return (
            <div
              key={c.id}
              className={`rounded-lg border p-4 transition-colors ${bgClass} ${borderClass}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <span className={`text-xs font-semibold ${authorClass}`}>
                  {c.authorName}
                  {isAgent && !isAi && <span className={`ml-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>· {t("ticketDetail.comments.agent")}</span>}
                  {isAi && <span className={`ml-1 font-medium ${isDarkMode ? 'text-violet-300' : 'text-violet-700'}`}>· {t("ticketDetail.comments.aiAgent")}</span>}
                  {isInternal && <span className={`ml-1 font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>· {t("ticketDetail.comments.internalNote")}</span>}
                </span>
                <span className={`text-[11px] ${timeClass}`}>{formatDate(c.createdAt)}</span>
              </div>
              {c.body && <p className={`whitespace-pre-wrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{c.body}</p>}
              <AttachmentList attachments={c.attachments} />
            </div>
          );
        })}
      </div>

      {onReply && (
        <div className={`mt-4 border-t ${isDarkMode ? 'border-dark-border' : 'border-gray-100'} pt-4`}>
          <textarea
            className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 ${
              isDarkMode
                ? 'border-dark-border bg-dark-card text-white focus:ring-gray-600'
                : 'border-gray-300 bg-white text-gray-900 focus:ring-gray-200'
            }`}
            rows={3}
            placeholder={replyLabel}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <input
                type="file"
                multiple
                className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
              {allowInternalToggle && !internalOnly && (
                <label className={`flex items-center gap-2 text-xs font-medium cursor-pointer ${
                  isDarkMode ? 'text-amber-300' : 'text-amber-700'
                }`}>
                  <input
                    type="checkbox"
                    checked={internal}
                    onChange={(e) => setInternal(e.target.checked)}
                    className="cursor-pointer"
                  />
                  {t("ticketDetail.comments.internalToggle")}
                </label>
              )}
            </div>
            <button
              onClick={send}
              disabled={sending}
              className="rounded-full px-6 py-2 text-sm font-semibold text-white transition-opacity disabled:opacity-50 hover:shadow-lg"
              style={{ backgroundColor: isInternalReply ? "#b45309" : brand.primary }}
            >
              {sending ? t("ticketDetail.comments.sending") : isInternalReply ? t("ticketDetail.comments.addInternalNote") : t("ticketDetail.comments.send")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
